import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  CellClickedEvent,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowDragEndEvent,
  RowDragEvent,
  RowDragMoveEvent,
  RowSelectionOptions,
  SelectionChangedEvent
} from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'
import CustomAgGrid from '../../../../../shared/components/custom/Grid/CustomAgGrid'
import agGridTheme from '../../../../../config/agGrid/theme'
import { getStatusBarConfig } from '../../../../../shared/utils/agGridConfig'
import { Dimension } from '../../../../../features/dimension/types/Dimension'
import { isOwnSource } from '../utils/dimensionAccess'
import { buildDropBatch } from '../utils/dimensionMutationBatch'
import { useDimensionGridActions } from '../hooks/useDimensionGridActions'
import {
  buildAutoGroupColumnDef,
  buildListColumnDefs,
  buildTreeColumnDefs,
  DimensionGridContext
} from './columnDefs/dimensionColumnDefs'

export type DimensionGridMode = 'tree' | 'list'
export type DimensionSourceFilter = 'all' | 'own' | 'integration'

interface DimensionGridProps {
  mode: DimensionGridMode
  flatList: Dimension[]
  loading?: boolean
  canEdit: boolean
  searchText: string
  sourceFilter: DimensionSourceFilter
  selectedIds: string[]
  clearSelectionSignal: number
  onSelectionChange: (ids: string[]) => void
  onActiveDimensionChange: (dimensionId: string | null) => void
  onRequestRename: (dimension: Dimension) => void
  onRequestMoveUnder: (dimensionIds: string[]) => void
  onRequestAddChild: (parentDimensionId: string) => void
  onRequestDelete: (dimensionIds: string[]) => void
}

const treeRowSelection: RowSelectionOptions<Dimension> = {
  mode: 'multiRow',
  checkboxLocation: 'autoGroupColumn',
  headerCheckbox: false,
  selectAll: 'filtered',
  enableClickSelection: false,
  groupSelects: 'self'
}

const listRowSelection: RowSelectionOptions<Dimension> = {
  mode: 'multiRow',
  checkboxes: true,
  headerCheckbox: true,
  selectAll: 'filtered',
  enableClickSelection: false
}

const noColumnBorderTheme = agGridTheme.withParams({ columnBorder: false })

const DimensionGrid: React.FC<DimensionGridProps> = ({
  mode,
  flatList,
  loading,
  canEdit,
  searchText,
  sourceFilter,
  selectedIds,
  clearSelectionSignal,
  onSelectionChange,
  onActiveDimensionChange,
  onRequestRename,
  onRequestMoveUnder,
  onRequestAddChild,
  onRequestDelete
}) => {
  const { t } = useTranslation()
  const gridApiRef = useRef<GridApi<Dimension> | null>(null)
  const { applyOrderPatch, duplicate, isCreating, isRenaming, isDeleting, isReordering, isSettingDefault } =
    useDimensionGridActions()
  const dragEnabled = mode === 'tree' && canEdit && !searchText
  const mutating = isCreating || isRenaming || isDeleting || isReordering || isSettingDefault

  const showTree = mode === 'tree'

  const dimensionNameById = useMemo(() => new Map(flatList.map(d => [d.dimensionId, d.name])), [flatList])

  const context = useMemo<DimensionGridContext>(
    () => ({
      flatList,
      canEdit,
      onRequestRename,
      onRequestMoveUnder,
      onRequestAddChild,
      onRequestDelete,
      onDuplicate: dimension => duplicate(dimension, t('dimensionsPage:duplicateSuffix', '(kopio)'))
    }),
    [flatList, canEdit, onRequestRename, onRequestMoveUnder, onRequestAddChild, onRequestDelete, duplicate, t]
  )

  const columnDefs = useMemo(
    () => (mode === 'tree' ? buildTreeColumnDefs(t, canEdit) : buildListColumnDefs(t, dimensionNameById, canEdit)),
    [mode, t, dimensionNameById, canEdit]
  )

  const activeRowSelection = useMemo(() => {
    if (!canEdit) return undefined
    if (mode === 'list') return listRowSelection
    return showTree ? treeRowSelection : undefined
  }, [canEdit, mode, showTree])

  const selectionColumnDef = useMemo(
    () => (mode === 'list' ? { pinned: 'left' as const, width: 44, resizable: false, lockPosition: true } : undefined),
    [mode]
  )

  const autoGroupColumnDef = useMemo(
    () => (showTree ? buildAutoGroupColumnDef(t, dragEnabled) : undefined),
    [showTree, t, dragEnabled]
  )

  const statusBar = useMemo(() => getStatusBarConfig(), [])

  const getRowId = useCallback((params: GetRowIdParams<Dimension>) => params.data.dimensionId, [])

  const isExternalFilterPresent = useCallback(
    () => (mode === 'tree' && !!searchText) || (mode === 'list' && sourceFilter !== 'all'),
    [mode, searchText, sourceFilter]
  )
  const doesExternalFilterPass = useCallback(
    (node: { data?: Dimension }) => {
      if (mode === 'tree') {
        return !searchText || !!node.data?.name.toLowerCase().includes(searchText.toLowerCase())
      }
      if (sourceFilter === 'own') return !!node.data && isOwnSource(node.data.source)
      if (sourceFilter === 'integration') return !!node.data && !isOwnSource(node.data.source)
      return true
    },
    [mode, searchText, sourceFilter]
  )

  // Read from a ref, not `selectedIds` directly: this must NOT change identity on every
  // click-driven selection change, or reapplying it after every click resets ag-grid's
  // internal "last selected row" anchor and breaks shift-click range selection.
  const selectedIdsRef = useRef(selectedIds)
  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  const applySelection = useCallback(() => {
    const api = gridApiRef.current
    if (!api) return
    const selected = new Set(selectedIdsRef.current)
    api.forEachNode(node => {
      if (node.data) node.setSelected(selected.has(node.data.dimensionId))
    })
  }, [])

  const handleGridReady = useCallback(
    (event: GridReadyEvent<Dimension>) => {
      gridApiRef.current = event.api
      applySelection()
    },
    [applySelection]
  )

  // Only re-apply on a mode switch (tree <-> list swaps treeData + columnDefs wholesale, which
  // isn't guaranteed to preserve selection). An ordinary rowData change (e.g. the list view's
  // source filter) is already handled natively by ag-grid via getRowId - no need to redo it here.
  useEffect(() => {
    applySelection()
  }, [applySelection, mode])

  useEffect(() => {
    gridApiRef.current?.onFilterChanged()
  }, [searchText, sourceFilter])

  // Explicit "Clear selection" from the action bar. Kept as a one-shot signal rather than reacting
  // to `selectedIds` emptying, so it never runs during normal click-driven selection changes.
  useEffect(() => {
    if (clearSelectionSignal > 0) gridApiRef.current?.deselectAll()
  }, [clearSelectionSignal])

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<Dimension>) => {
      onSelectionChange(event.api.getSelectedRows().map(r => r.dimensionId))
    },
    [onSelectionChange]
  )

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<Dimension>) => {
      const colId = event.column.getColId()
      if (colId === 'rowMenu' || !event.data) return
      onActiveDimensionChange(event.data.dimensionId)
    },
    [onActiveDimensionChange]
  )

  const handleRowDragMove = useCallback(
    (event: RowDragMoveEvent<Dimension>) => {
      const sourceId = event.node.data?.dimensionId
      if (!sourceId) return
      const { indicator } = buildDropBatch(
        flatList,
        sourceId,
        event.overNode?.data?.dimensionId ?? null,
        !!event.event?.shiftKey,
        event.vDirection === 'up'
      )
      const targetNode = indicator ? event.api.getRowNode(indicator.targetDimensionId) : undefined
      event.api.setRowDropPositionIndicator(
        indicator && targetNode ? { row: targetNode, dropIndicatorPosition: indicator.position } : null
      )
    },
    [flatList]
  )

  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent<Dimension>) => {
      event.api.setRowDropPositionIndicator(null)
      const sourceId = event.node.data?.dimensionId
      if (!sourceId) return
      const { patch } = buildDropBatch(
        flatList,
        sourceId,
        event.overNode?.data?.dimensionId ?? null,
        !!event.event?.shiftKey,
        event.vDirection === 'up'
      )
      if (patch.length) applyOrderPatch(patch)
    },
    [flatList, applyOrderPatch]
  )

  const handleRowDragCancel = useCallback((event: RowDragEvent<Dimension>) => {
    event.api.setRowDropPositionIndicator(null)
  }, [])

  return (
    <div style={{ height: 650 }}>
      <CustomAgGrid
        theme={noColumnBorderTheme}
        animateRows={false}
        rowData={flatList}
        loading={loading || mutating}
        columnDefs={columnDefs}
        getRowId={getRowId}
        context={context}
        rowSelection={activeRowSelection}
        selectionColumnDef={selectionColumnDef}
        onSelectionChanged={handleSelectionChanged}
        onCellClicked={handleCellClicked}
        onGridReady={handleGridReady}
        onRowDragMove={dragEnabled ? handleRowDragMove : undefined}
        onRowDragEnd={dragEnabled ? handleRowDragEnd : undefined}
        onRowDragCancel={dragEnabled ? handleRowDragCancel : undefined}
        quickFilterText={mode === 'list' ? searchText : undefined}
        isExternalFilterPresent={isExternalFilterPresent}
        doesExternalFilterPass={doesExternalFilterPass}
        treeData={showTree}
        treeDataParentIdField={showTree ? 'parentDimensionId' : undefined}
        groupDefaultExpanded={showTree ? 0 : undefined}
        autoGroupColumnDef={autoGroupColumnDef}
        excludeChildrenWhenTreeDataFiltering={showTree}
        statusBar={statusBar}
      />
    </div>
  )
}

export default DimensionGrid
